import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
import { ProgramContextRequest } from './ProgramContextRequest';
import { ProgramFollowOn } from './ProgramFollowOn';
export function ProgramReferences({ landscapeId }) {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const location = useLocation();
    const references = useQuery({
        queryKey: [
            'landscapes',
            'program-references',
            user?.tenant_id,
            user?.id,
            landscapeId
        ],
        queryFn: ()=>contractsClient.listLandscapeProgramReferences({
                landscapeId
            }),
        enabled: !!user?.tenant_id && !!user.id && !!landscapeId
    });
    const selected = params.get('reference');
    const selectPath = (id)=>{
        const next = new URLSearchParams(params);
        if (id) next.set('reference', id);
        else next.delete('reference');
        return `${location.pathname}?${next}`;
    };
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.landscapes.programReferences,
        "aria-label": "Program references",
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-base font-semibold",
                children: "Program references & follow-on context"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Permitted reusable context only. A follow-on signal is research, not a won deal. An end date does not confirm a recompete date."
            }),
            references.isLoading && /*#__PURE__*/ _jsxs("output", {
                "aria-label": "Loading Program references",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-24 w-full"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Loading Program references…"
                    })
                ]
            }),
            references.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Could not load Program references."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.landscapes.programReferenceRetry,
                        disabled: references.isFetching,
                        onClick: ()=>void references.refetch(),
                        children: "Retry references"
                    })
                ]
            }),
            !references.isLoading && !references.isError && references.data?.references.length === 0 && /*#__PURE__*/ _jsx("p", {
                children: "No permitted Program context is available for this Landscape."
            }),
            (references.isFetching || references.isError ? [] : references.data?.references ?? []).map((reference)=>/*#__PURE__*/ _jsx(SpecraCard, {
                    "data-testid": Testids.landscapes.programReferenceCard,
                    "data-program-id": reference.programId,
                    children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "outline",
                                children: "Program reference"
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                className: "font-semibold",
                                children: reference.title
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: reference.capabilities.join(' · ') || 'Capabilities not supplied'
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                "data-testid": Testids.landscapes.programReferenceView,
                                to: selectPath(reference.releaseId),
                                state: location.state,
                                children: "View Reference"
                            }),
                            selected === reference.releaseId && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.landscapes.programReferenceDetail,
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: reference.summary
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Start date: ",
                                            reference.startDate || 'Not supplied',
                                            " · End date:",
                                            ' ',
                                            reference.endDate || 'Not supplied'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "End date is not a confirmed recompete date."
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Source: Library release ",
                                            reference.releaseId,
                                            " · Version",
                                            ' ',
                                            reference.releaseVersion,
                                            " · Released as of",
                                            ' ',
                                            reference.releasedAt?.toDate().toLocaleString('en-US', {
                                                timeZone: 'UTC'
                                            }) ?? 'Not supplied',
                                            ' ',
                                            "UTC"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "This timestamp dates the Library publication, not a Registry contract observation. Registry observations retain their own source and effective/retrieval dates."
                                    }),
                                    /*#__PURE__*/ _jsx(ProgramContextRequest, {
                                        context: `Landscape: ${landscapeId}\nProgram reference: ${reference.title} (${reference.programId})\nLibrary item: ${reference.libraryItemId}\nLibrary release: ${reference.releaseId} · version ${reference.releaseVersion}\nPublished: ${reference.releasedAt?.toDate().toISOString() ?? 'Not supplied'}\nReturn: ${location.pathname + location.search}`
                                    }, reference.releaseId),
                                    reference.contractId && reference.contractIdentity && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    "Registry scope: ",
                                                    reference.contractIdentity.role,
                                                    " ·",
                                                    ' ',
                                                    reference.contractIdentity.partyKey || 'Whole contract',
                                                    " ·",
                                                    ' ',
                                                    reference.contractIdentity.agency,
                                                    " · ",
                                                    reference.contractIdentity.subtier,
                                                    " · Parent ",
                                                    reference.contractIdentity.referencedIdv || 'Not supplied'
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Link, {
                                                to: `/registries?tab=contracts&record=${encodeURIComponent(reference.contractId)}`,
                                                state: {
                                                    returnTo: location.pathname + location.search
                                                },
                                                children: [
                                                    "Registry contract ",
                                                    reference.contractIdentity.piid
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                children: "Contract observation provenance (up to 20 permitted completion records; more in Registry):"
                                            }),
                                            reference.contractEvidence.length === 0 && /*#__PURE__*/ _jsx("p", {
                                                children: "No permitted completion observation source/as-of is supplied. Library release time is not contract observation time."
                                            }),
                                            reference.contractEvidence.map((evidence)=>/*#__PURE__*/ _jsxs("p", {
                                                    children: [
                                                        "Source: ",
                                                        evidence.source,
                                                        " · Version",
                                                        ' ',
                                                        evidence.sourceVersion || 'Not supplied',
                                                        " · Observation",
                                                        ' ',
                                                        evidence.observationId,
                                                        " · Effective as of",
                                                        ' ',
                                                        evidence.effectiveAt?.toDate().toISOString() ?? 'Unknown',
                                                        " · Retrieved",
                                                        ' ',
                                                        evidence.retrievedAt?.toDate().toISOString() ?? 'Unknown'
                                                    ]
                                                }, evidence.evidenceId))
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "Existing authorized threads in this Landscape:"
                                    }),
                                    reference.threads.map((thread)=>/*#__PURE__*/ _jsx(Link, {
                                            className: "block",
                                            to: `/opportunities?watchOpportunity=${encodeURIComponent(thread.opportunityId)}`,
                                            state: {
                                                returnTo: location.pathname + location.search
                                            },
                                            children: thread.title
                                        }, thread.opportunityId)),
                                    /*#__PURE__*/ _jsx(ProgramFollowOn, {
                                        landscapeId: landscapeId,
                                        reference: reference
                                    }, `follow-on:${reference.releaseId}`),
                                    reference.canOpenDelivery && /*#__PURE__*/ _jsx(Link, {
                                        "data-testid": Testids.landscapes.programReferenceDelivery,
                                        to: `/delivery/programs/${encodeURIComponent(reference.programId)}`,
                                        state: {
                                            returnTo: location.pathname + location.search
                                        },
                                        children: "Open Delivery"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: selectPath(),
                                        state: location.state,
                                        children: "Close reference"
                                    })
                                ]
                            })
                        ]
                    })
                }, reference.releaseId)),
            /*#__PURE__*/ _jsx(ProgramContextRequest, {
                context: `Landscape: ${landscapeId}\nNo additional permitted source context is assumed.\nReturn: ${location.pathname + location.search}`
            })
        ]
    });
}
